import React, {
    FC,
    MouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react"
import { Chip, ChipProps } from "dash-styled"
import { FeedbackTagStats } from "../../../../../../Models/API/AppInstanceModel"
import { useDispatch } from "react-redux"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../Routes"
import {
    FeedbackMessageFilterOperator,
    FilterExpression
} from "../../../../../../Models/Client/FilterExpression"
import {
    APIJobQueryModel,
    FeedbackMessageFilter
} from "../../../../../../Models/API/JobModel"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { useFeedbackTags } from "../../../../../../Hooks/useFeedbackTags"
import { getFeedbackTagColor } from "../Common/constants"
import { getValue } from "../../../../../../Models/Client/FetchState"
import {
    groupTagsByParent,
    sortAndFlattenGroupedTagsByPosition,
    sortTagsByJobAndPosition
} from "../../../../../../Models/API/JobFeedbackModel"

interface FeedbackTagChipProps {
    value: string
    onClick?: (evt: MouseEvent<HTMLDivElement>) => void
    color?: ChipProps["color"]
    className?: string
    chipClassName?: string
}
export const FeedbackTagChip: FC<FeedbackTagChipProps> = ({
    value,
    onClick,
    color,
    className,
    chipClassName
}) => (
    <div onClick={onClick} className={`cursor-pointer ${className}`}>
        <Chip
            testId="job-feedback-tag"
            size="sm"
            className={`mr-1 ${chipClassName}`}
            color={color}
        >
            {value}
        </Chip>
    </div>
)

interface FeedbackTagsHeaderProps {
    orgUsername: string
    appID: string
    feedbackTags: FeedbackTagStats[]
    sort: SortExpression<APIJobQueryModel>
    showOnlyChildTags?: boolean
    appliedFilters?: FilterExpression<APIJobQueryModel>[]
}
export const FeedbackTagsHeader: FC<FeedbackTagsHeaderProps> = ({
    orgUsername,
    appID,
    feedbackTags,
    sort,
    showOnlyChildTags = false,
    appliedFilters
}) => {
    const [selectedTag, setSelectedTag] = useState<FeedbackTagStats>()
    const dispatch = useDispatch<MainThunkDispatch>()
    const navTo = useCallback(
        (path: string) => dispatch(push(path)),
        [dispatch]
    )

    const appFeedbackTags = useFeedbackTags(appID)

    useEffect(() => {
        const appliedTag = appliedFilters?.find(f => f.fieldName === "messages")

        setSelectedTag(
            feedbackTags.find(
                tag =>
                    tag.id ===
                    (appliedTag?.value as FeedbackMessageFilter)?.feedbackTags
            )
        )
    }, [appliedFilters, feedbackTags])

    const setFeedbackTag = useCallback(
        (feedbackTag?: number) =>
            navTo(
                routePaths.appDetailMessages(orgUsername, appID, {
                    filter: feedbackTag
                        ? [
                              {
                                  fieldName: "messages",
                                  operator:
                                      FeedbackMessageFilterOperator.isOneOfTag,
                                  value: { feedbackTags: feedbackTag }
                              }
                          ]
                        : [],
                    sort
                })
            ),
        [navTo, orgUsername, appID, sort]
    )

    const handleTagClick = useCallback(
        (tag: FeedbackTagStats) => (evt: MouseEvent<HTMLDivElement>) => {
            evt.preventDefault()
            evt.stopPropagation()
            if (!showOnlyChildTags) setSelectedTag(tag)
            setFeedbackTag(tag.id)
        },
        [setFeedbackTag, showOnlyChildTags]
    )

    const handleReset = useCallback(() => {
        setSelectedTag(undefined)
        setFeedbackTag(undefined)
    }, [setFeedbackTag])

    const tagsToDisplay: [FeedbackTagStats, ChipProps["color"] | undefined][] =
        useMemo(() => {
            if (!feedbackTags) {
                return []
            } else if (!selectedTag) {
                return (
                    showOnlyChildTags
                        ? sortAndFlattenGroupedTagsByPosition(
                              groupTagsByParent(feedbackTags)
                          )
                        : feedbackTags
                              .filter(
                                  tag =>
                                      tag.position > -3 &&
                                      ((tag.jobCount === undefined &&
                                          !tag.parentId) ||
                                          (tag.jobCount !== undefined &&
                                              tag.jobCount > 0 &&
                                              !tag.parentId))
                              )
                              .sort(sortTagsByJobAndPosition)
                ).map(tag => [
                    tag,
                    getFeedbackTagColor(tag, getValue(appFeedbackTags))
                ])
            }
            const childTags = feedbackTags.filter(
                tag =>
                    (tag.jobCount === undefined &&
                        tag.parentId === selectedTag.id) ||
                    (tag.jobCount !== undefined &&
                        tag.jobCount > 0 &&
                        tag.parentId === selectedTag.id)
            )
            return (childTags.length > 0 ? childTags : [selectedTag])
                .sort(sortTagsByJobAndPosition)
                .map(tag => [
                    tag,
                    getFeedbackTagColor(tag, getValue(appFeedbackTags))
                ])
        }, [appFeedbackTags, feedbackTags, selectedTag, showOnlyChildTags])

    if (tagsToDisplay.length === 0) {
        return null
    }

    return (
        <div
            className={`py-2 mr-1 flex items-center ${
                showOnlyChildTags ? "" : "flex-wrap"
            }`}
        >
            {tagsToDisplay.map(([tag, color]) => (
                <FeedbackTagChip
                    key={tag.id}
                    value={`${tag.tag}${
                        tag.jobCount !== undefined ? ` (${tag.jobCount})` : ""
                    }`}
                    onClick={handleTagClick(tag)}
                    color={color}
                    className={!showOnlyChildTags ? "mr-1.5" : ""}
                    chipClassName={!showOnlyChildTags ? "my-0.5" : ""}
                />
            ))}
            {selectedTag && !showOnlyChildTags && (
                <button
                    type="button"
                    onClick={handleReset}
                    className="flex items-center text-pink-300"
                >
                    Clear filters
                </button>
            )}
        </div>
    )
}
